import { motion } from 'framer-motion';
import { useData } from 'vike-react/useData';
import { getAudienceById } from '@shared/constants/audiences';
import { CountUp } from '../../components/motion/CountUp';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { JourneyTimeline } from '../../components/blocks/JourneyTimeline';
import { ToolsForAudience } from '../../components/blocks/ToolsForAudience';
import { ConcreteCase } from '../../components/blocks/ConcreteCase';
import { SectorHero } from '../../components/sector/SectorHero';
import { SectorProof } from '../../components/sector/SectorProof';
import { SectorFAQ } from '../../components/sector/SectorFAQ';
import { SectorCTA } from '../../components/sector/SectorCTA';
import { getAudienceVoice } from '../../config/audience-voice';
import { audienceLegacyTone } from '../../config/audience-theme';
import { easeStripe } from '../../lib/motion';
import type { Data } from './+data';

const AUDIENCE_ID = 'artisans' as const;

export default function ArtisansPage() {
  const { journey, tools, cases } = useData<Data>();
  const audience = getAudienceById(AUDIENCE_ID)!;
  const voice = getAudienceVoice(AUDIENCE_ID);
  const legacyTone = audienceLegacyTone(AUDIENCE_ID);

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Artisans RGE', url: 'https://solutionecofrance.fr/artisans' },
        ]}
      />

      <SectorHero
        audience={audience}
        voice={voice}
        sideSlot={<ArtisanDashboardPreview />}
      />

      <SectorProof audience={audience} voice={voice} variant="bento" />

      {/* Méthode : on ancre #methode pour le secondaryCta du hero */}
      <div id="methode" />
      {journey && (
        <JourneyTimeline
          eyebrow={voice.method.eyebrow}
          title={voice.method.title}
          description={voice.method.description}
          totalDuration={journey.totalDuration}
          ctaLabel={journey.ctaLabel}
          ctaHref={journey.ctaHref}
          steps={journey.steps}
          tone={legacyTone}
        />
      )}

      {tools.length > 0 && (
        <ToolsForAudience
          eyebrow={voice.tools.eyebrow}
          title={voice.tools.title}
          description={voice.tools.description}
          tools={tools.map((t) => ({
            title: t.title,
            description: t.description,
            iconName: t.iconName,
            href: t.href,
            badge: t.badge,
            metric: t.metric,
            metricLabel: t.metricLabel,
          }))}
          tone={legacyTone}
        />
      )}

      {cases.length > 0 && (
        <ConcreteCase
          eyebrow={voice.caseStudy.eyebrow}
          title={voice.caseStudy.title}
          cases={cases}
          tone={legacyTone}
        />
      )}

      <SectorFAQ audience={audience} voice={voice} />

      <SectorCTA audience={audience} voice={voice} id="devenir-partenaire" />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// ArtisanDashboardPreview — side card du hero spécifique au pôle phare.
// Mock visuel d'un tableau de bord artisan (live · 12 dossiers · 42 800 €).
// ════════════════════════════════════════════════════════════════

function ArtisanDashboardPreview() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.18, ease: easeStripe }}
      className="rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] ring-1 ring-inset ring-accent-200/60 md:p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
            Tableau de bord artisan
          </p>
          <p className="mt-0.5 text-xs text-stone-500">12 dossiers en cours</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 ring-1 ring-inset ring-brand-200/50">
          <span className="h-1 w-1 rounded-full bg-brand-500 animate-glow-pulse" />
          Live
        </span>
      </div>

      <div className="mt-5 flex items-baseline gap-2 font-display tracking-tight">
        <span className="text-3xl font-bold text-ink md:text-4xl">
          <CountUp end={42800} />
        </span>
        <span className="text-base text-stone-500">€ ce trimestre</span>
      </div>

      <div className="mt-5 space-y-1.5">
        {[
          {
            label: 'PAC air/eau résidentielle — fiche BAR-TH-171',
            val: '4 800 €',
            status: 'Payé',
            tone: 'brand',
          },
          {
            label: 'Isolation combles — fiche BAR-EN-101',
            val: '2 100 €',
            status: 'Validé',
            tone: 'lavender',
          },
          {
            label: 'Chaudière biomasse (fiche BAT-TH-113)',
            val: '12 600 €',
            status: 'Instruction',
            tone: 'accent',
          },
        ].map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between rounded-lg border border-stone-200/70 bg-stone-50/40 px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs text-stone-700">{row.label}</p>
              <p
                className={`mt-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                  row.tone === 'brand'
                    ? 'text-brand-700'
                    : row.tone === 'lavender'
                      ? 'text-lavender-700'
                      : 'text-accent-800'
                }`}
              >
                {row.status}
              </p>
            </div>
            <span className="ml-2 font-mono text-xs font-semibold text-ink tabular-nums">
              {row.val}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-stone-200/60 pt-3">
        <p className="text-[11px] text-stone-500">Prochain virement</p>
        <p className="font-mono text-[12px] font-semibold text-brand-700 tabular-nums">
          5 jours
        </p>
      </div>
    </motion.div>
  );
}
